import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
    Plus, Search, Box, Edit, Trash2, Check, Upload, FileSpreadsheet,
    Wine, Coffee, Droplet, QrCode, Download
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UNIT_OPTIONS = [
    { value: 'cl', label: 'CL' },
    { value: 'litre', label: 'Litre' },
    { value: 'şişe', label: 'Şişe' },
    { value: 'paket', label: 'Paket' },
    { value: 'kg', label: 'KG' },
    { value: 'adet', label: 'Adet' },
    { value: 'koli', label: 'Koli' },
    { value: 'box', label: 'Box' },
    { value: 'bidon', label: 'Bidon' },
    { value: 'sarf', label: 'Sarf Malzemesi' },
    { value: 'diğer', label: 'Diğer' },
];

const CATEGORY_OPTIONS = [
    { value: 'alkollü', label: 'Alkollü İçecekler', icon: Wine, color: 'bg-purple-100 text-purple-700' },
    { value: 'alkolsüz', label: 'Alkolsüz İçecekler', icon: Coffee, color: 'bg-blue-100 text-blue-700' },
    { value: 'soft', label: 'Soft İçecekler', icon: Droplet, color: 'bg-green-100 text-green-700' },
    { value: 'yiyecek', label: 'Yiyecek', icon: Coffee, color: 'bg-orange-100 text-orange-700' },
    { value: 'temizlik', label: 'Temizlik', icon: Droplet, color: 'bg-cyan-100 text-cyan-700' },
    { value: 'sarf', label: 'Sarf Malzemeleri', icon: Droplet, color: 'bg-amber-100 text-amber-700' },
    { value: 'demirbaş', label: 'Demirbaş', icon: Droplet, color: 'bg-slate-200 text-slate-700' },
    { value: 'diğer', label: 'Diğer', icon: Droplet, color: 'bg-gray-100 text-gray-700' },
];

export function Products() {
    const { isAdmin } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [importData, setImportData] = useState('');
    const fileInputRef = useRef(null);
    const sedFileRef = useRef(null);
    const [tcmbRate, setTcmbRate] = useState(35.5);
    const [editingCell, setEditingCell] = useState(null); // {productId, field}
    const [editValue, setEditValue] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());

    const [formData, setFormData] = useState({
        sedna_code: '',
        name: '',
        unit: 'adet',
        category: 'soft',
        price_tl: '',
        price_eur: '',
        porsiyon: '',
        notes: '',
    });

    useEffect(() => {
        loadProducts();
        loadTCMBRate();
    }, [categoryFilter]);

    const loadTCMBRate = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/tcmb-rate`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setTcmbRate(data.rate || 35.5);
            }
        } catch (error) {
            console.error('TCMB rate error:', error);
        }
    };

    const handlePriceTLChange = (value) => {
        const priceTL = parseFloat(value) || 0;
        const priceEUR = tcmbRate > 0 ? (priceTL / tcmbRate).toFixed(2) : '';
        setFormData({...formData, price_tl: value, price_eur: priceEUR});
    };

    const loadProducts = async (searchTerm = '') => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
            
            const url = `${API_URL}/api/products${params.toString() ? '?' + params.toString() : ''}`;
            
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setProducts(data);
            }
        } catch (error) {
            console.error('Load error:', error);
            toast.error('Ürünler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        loadProducts(value);
    };

    const openCreateDialog = () => {
        setEditingProduct(null);
        setFormData({
            sedna_code: '',
            name: '',
            unit: 'adet',
            category: 'soft',
            price_tl: '',
            price_eur: '',
            porsiyon: '',
            notes: '',
        });
        setDialogOpen(true);
    };

    const openEditDialog = (product) => {
        setEditingProduct(product);
        setFormData({
            sedna_code: product.sedna_code || product.product_code || product.code || '',
            name: product.name || '',
            unit: product.unit || 'adet',
            category: product.category || 'soft',
            price_tl: product.price_tl?.toString() || '',
            price_eur: product.price_eur?.toString() || '',
            porsiyon: product.porsiyon != null ? product.porsiyon.toString() : '',
            notes: product.notes || '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Ürün adı zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingProduct 
                ? `${API_URL}/api/products/${editingProduct.id}`
                : `${API_URL}/api/products`;
            
            const payload = {
                ...formData,
                price_tl: parseFloat(formData.price_tl) || 0,
                price_eur: parseFloat(formData.price_eur) || 0,
                porsiyon: formData.porsiyon === '' ? null : parseFloat(formData.porsiyon) || null,
            };

            const res = await fetch(url, {
                method: editingProduct ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                toast.success(editingProduct ? 'Ürün güncellendi' : 'Ürün eklendi');
                // Sedna kodu mükerrer uyarısı (kayıt yine oldu, sadece bilgilendirme)
                if (data && data.warning) {
                    toast.warning(data.warning);
                }
                setDialogOpen(false);
                loadProducts(search);
            } else {
                toast.error('İşlem başarısız');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleDelete = async (product) => {
        if (!window.confirm(`"${product.name}" ürününü silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/products/${product.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                toast.success('Ürün silindi');
                loadProducts(search);
            } else {
                toast.error('Silme işlemi başarısız');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const toggleSelect = (productId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(productId)) next.delete(productId);
            else next.add(productId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === products.length && products.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(products.map(p => p.id)));
        }
    };

    const handleBulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            toast.error('Silmek için ürün seçin');
            return;
        }
        if (!window.confirm(`Seçili ${ids.length} ürünü silmek istediğinize emin misiniz?`)) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/products/bulk-delete`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_ids: ids }),
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`${data.deleted} ürün silindi`);
                setSelectedIds(new Set());
                loadProducts(search);
            } else {
                toast.error('Toplu silme başarısız');
            }
        } catch {
            toast.error('Bir hata oluştu');
        }
    };

    const handleClearAll = async () => {
        if (!window.confirm(`TÜM ürünleri silmek istediğinize emin misiniz? Bu işlem geri alınamaz!`)) return;
        if (!window.confirm(`SON UYARI: ${products.length} ürünün TAMAMI silinecek. Onaylıyor musunuz?`)) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/products-all/clear`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Tüm ürünler silindi (${data.deleted})`);
                setSelectedIds(new Set());
                loadProducts(search);
            } else {
                toast.error('İşlem başarısız');
            }
        } catch {
            toast.error('Bir hata oluştu');
        }
    };

    // Inline edit: save a single field
    const inlineSave = async (product, field, value) => {
        try {
            const token = localStorage.getItem('token');
            let numValue;
            if (field === 'price_tl' || field === 'price_eur') {
                numValue = parseFloat(value) || 0;
            } else if (field === 'porsiyon') {
                numValue = value === '' || value === null ? null : (parseFloat(value) || null);
            } else {
                numValue = value;
            }
            
            const payload = {
                name: product.name,
                unit: product.unit,
                category: product.category,
                price_tl: product.price_tl || 0,
                price_eur: product.price_eur || 0,
                porsiyon: product.porsiyon != null ? product.porsiyon : null,
                notes: product.notes || '',
                [field]: numValue,
            };
            
            // Auto-calculate EUR when TL changes
            if (field === 'price_tl' && tcmbRate > 0) {
                payload.price_eur = parseFloat((numValue / tcmbRate).toFixed(2));
            }
            
            const res = await fetch(`${API_URL}/api/products/${product.id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                setProducts(prev => prev.map(p => p.id === product.id ? {
                    ...p,
                    [field]: numValue,
                    ...(field === 'price_tl' && tcmbRate > 0 ? { price_eur: payload.price_eur } : {})
                } : p));
                toast.success('Güncellendi');
                if (data && data.warning) toast.warning(data.warning);
            } else {
                toast.error('Güncelleme başarısız');
            }
        } catch { toast.error('Hata oluştu'); }
        setEditingCell(null);
    };

    const startEdit = (productId, field, currentValue) => {
        setEditingCell({ productId, field });
        setEditValue(currentValue?.toString() || '');
    };

    const handleEditKeyDown = (e, product, field) => {
        if (e.key === 'Enter') {
            inlineSave(product, field, editValue);
        } else if (e.key === 'Escape') {
            setEditingCell(null);
        }
    };

    const handleBulkImport = async () => {
        if (!importData.trim()) {
            toast.error('Lütfen veri girin');
            return;
        }

        try {
            const lines = importData.trim().split('\n');
            const items = [];
            
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;
                
                const parts = line.includes('\t') ? line.split('\t') : line.split(';');
                
                if (parts.length >= 1) {
                    items.push({
                        name: parts[0]?.trim() || '',
                        unit: parts[1]?.trim()?.toLowerCase() || 'adet',
                        category: parts[2]?.trim()?.toLowerCase() || 'soft',
                        price_tl: parseFloat(parts[3]?.trim()) || 0,
                        price_eur: parseFloat(parts[4]?.trim()) || 0,
                    });
                }
            }

            if (items.length === 0) {
                toast.error('Geçerli veri bulunamadı');
                return;
            }

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/products/bulk-import`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(items),
            });

            if (res.ok) {
                const result = await res.json();
                toast.success(`${result.imported} ürün başarıyla eklendi`);
                setImportDialogOpen(false);
                setImportData('');
                loadProducts(search);
            } else {
                toast.error('Import işlemi başarısız');
            }
        } catch (error) {
            console.error('Import error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
            handleExcelUpload(file);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setImportData(event.target.result);
        };
        reader.readAsText(file);
    };

    const handleSednaCsvUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const token = localStorage.getItem('token');
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${API_URL}/api/products/import-sedna-codes`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd,
            });
            if (res.ok) {
                const r = await res.json();
                toast.success(`${r.updated} ürünün Sedna kodu güncellendi`);
                if (r.unmatched_count > 0) {
                    toast.warning(`${r.unmatched_count} ürün adı sistemde eşleşmedi (örn: ${r.unmatched_sample.slice(0,3).map(x=>x.name).join(', ')}...)`);
                }
                if (r.csv_duplicates_count > 0) {
                    toast.warning(`CSV'de ${r.csv_duplicates_count} mükerrer kod satırı var`);
                }
                loadProducts(search);
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'CSV yüklenemedi');
            }
        } catch {
            toast.error('CSV yüklenirken hata');
        } finally {
            // Reset input so same file can be re-uploaded
            if (sedFileRef.current) sedFileRef.current.value = '';
        }
    };

    const handleExcelUpload = async (file) => {
        try {
            const token = localStorage.getItem('token');
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);

            const res = await fetch(`${API_URL}/api/products/bulk-import`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formDataUpload,
            });

            if (res.ok) {
                const result = await res.json();
                toast.success(`${result.imported} ürün eklendi, ${result.skipped} atlandı (zaten var)`);
                if (result.errors?.length > 0) {
                    toast.error(`${result.errors.length} satırda hata`);
                }
                setImportDialogOpen(false);
                loadProducts(search);
            } else {
                toast.error('Excel import başarısız');
            }
        } catch (error) {
            console.error('Excel import error:', error);
            toast.error('Excel yükleme hatası');
        }
    };

    const downloadQRLabels = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/products/qr-labels`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'urun_qr_etiketleri.pdf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('QR etiketleri indirildi');
            } else {
                toast.error('QR etiket oluşturulamadı');
            }
        } catch (error) {
            toast.error('QR indirme hatası');
        }
    };

    const getCategoryBadge = (category) => {
        const cat = CATEGORY_OPTIONS.find(c => c.value === category);
        if (!cat) return <Badge variant="outline">{category}</Badge>;
        const Icon = cat.icon;
        return (
            <Badge className={cat.color}>
                <Icon className="w-3 h-3 mr-1" />
                {cat.label}
            </Badge>
        );
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="products-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Ürün Yönetimi</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Toplam {products.length} ürün kayıtlı
                    </p>
                </div>
                {isAdmin && (
                    <div className="flex flex-wrap gap-2">
                        {selectedIds.size > 0 && (
                            <Button
                                variant="destructive"
                                onClick={handleBulkDelete}
                                data-testid="bulk-delete-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Seçilenleri Sil ({selectedIds.size})
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={handleClearAll}
                            data-testid="clear-all-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Tümünü Sil
                        </Button>
                        <Button
                            variant="outline"
                            onClick={downloadQRLabels}
                            data-testid="qr-labels-btn"
                        >
                            <QrCode className="w-4 h-4 mr-2" />
                            QR Etiketleri İndir
                        </Button>
                        <Button 
                            variant="outline"
                            onClick={() => setImportDialogOpen(true)}
                            data-testid="import-products-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Toplu Ekle
                        </Button>
                        <Button 
                            variant="outline"
                            onClick={() => sedFileRef.current?.click()}
                            data-testid="import-sedna-btn"
                            title="CSV yükleyerek mevcut ürünlerin Sedna kodlarını toplu doldur"
                        >
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Sedna Kod İçe Aktar
                        </Button>
                        <input
                            ref={sedFileRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={handleSednaCsvUpload}
                            data-testid="sedna-csv-input"
                        />
                        <Button 
                            onClick={openCreateDialog}
                            className="bg-orwion-green hover:bg-orwion-green-hover" 
                            data-testid="create-product-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Yeni Ürün
                        </Button>
                    </div>
                )}
            </div>

            {/* Search & Filter */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Ürün adı ile ara..."
                                value={search}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                                <SelectValue placeholder="Kategori" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                                {CATEGORY_OPTIONS.map(cat => (
                                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Products List */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                        <tr className="bg-slate-50 border-b">
                            {isAdmin && (
                                <th className="p-4 w-10">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.size === products.length && products.length > 0}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 cursor-pointer"
                                        data-testid="select-all-checkbox"
                                    />
                                </th>
                            )}
                            <th className="text-left p-4 font-medium text-slate-600">Sedna Ürün Kodu</th>
                            <th className="text-left p-4 font-medium text-slate-600">Ürün Adı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Birim</th>
                            <th className="text-left p-4 font-medium text-slate-600">Kategori</th>
                            <th className="text-left p-4 font-medium text-slate-600">Fiyat (TL)</th>
                            <th className="text-left p-4 font-medium text-slate-600">Fiyat (EUR)</th>
                            <th className="text-left p-4 font-medium text-slate-600">Porsiyon</th>
                            <th className="text-right p-4 font-medium text-slate-600">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.length === 0 ? (
                            <tr>
                                <td colSpan={isAdmin ? "8" : "7"} className="p-8 text-center text-slate-500">
                                    <Box className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Henüz ürün kaydı yok</p>
                                </td>
                            </tr>
                        ) : (
                            products.map((product) => (
                                <tr key={product.id} className={`border-b hover:bg-slate-50 ${selectedIds.has(product.id) ? 'bg-blue-50' : ''}`} data-testid={`product-row-${product.id}`}>
                                    {isAdmin && (
                                        <td className="p-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(product.id)}
                                                onChange={() => toggleSelect(product.id)}
                                                className="w-4 h-4 cursor-pointer"
                                                data-testid={`select-product-${product.id}`}
                                            />
                                        </td>
                                    )}
                                    {/* Sedna Ürün Kodu - tıkla düzenle */}
                                    <td className="p-4">
                                        {editingCell?.productId === product.id && editingCell?.field === 'sedna_code' ? (
                                            <Input
                                                autoFocus
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => handleEditKeyDown(e, product, 'sedna_code')}
                                                onBlur={() => inlineSave(product, 'sedna_code', editValue)}
                                                className="h-8 text-sm w-32"
                                                placeholder="örn. 1000003"
                                                data-testid={`edit-sedna-code-${product.id}`}
                                            />
                                        ) : (
                                            <div className="flex items-center gap-1 cursor-pointer group"
                                                onClick={() => startEdit(product.id, 'sedna_code', product.sedna_code || product.product_code || product.code || '')}>
                                                <span className={`font-mono text-xs px-2 py-0.5 rounded ${(product.sedna_code || product.product_code || product.code) ? 'bg-slate-100 text-slate-700' : 'text-slate-300 italic'}`}>
                                                    {product.sedna_code || product.product_code || product.code || '—'}
                                                </span>
                                                <Edit className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        )}
                                    </td>
                                    {/* Ürün Adı - tıkla düzenle */}
                                    <td className="p-4">
                                        {editingCell?.productId === product.id && editingCell?.field === 'name' ? (
                                            <Input
                                                autoFocus
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => handleEditKeyDown(e, product, 'name')}
                                                onBlur={() => inlineSave(product, 'name', editValue)}
                                                className="h-8 text-sm"
                                                data-testid={`edit-name-${product.id}`}
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2 cursor-pointer group"
                                                onClick={() => startEdit(product.id, 'name', product.name)}>
                                                <Box className="w-4 h-4 text-slate-400" />
                                                <span className="font-medium">{product.name}</span>
                                                <Edit className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        )}
                                    </td>
                                    {/* Birim - açılır menü */}
                                    <td className="p-4">
                                        <Select value={product.unit || 'adet'} onValueChange={(v) => inlineSave(product, 'unit', v)}>
                                            <SelectTrigger className="h-8 w-24 text-xs" data-testid={`unit-select-${product.id}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {UNIT_OPTIONS.map(u => (
                                                    <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    {/* Kategori - açılır menü */}
                                    <td className="p-4">
                                        <Select value={product.category || 'soft'} onValueChange={(v) => inlineSave(product, 'category', v)}>
                                            <SelectTrigger className="h-8 w-40 text-xs" data-testid={`category-select-${product.id}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CATEGORY_OPTIONS.map(cat => (
                                                    <SelectItem key={cat.value} value={cat.value}>
                                                        <span className="flex items-center gap-1">
                                                            <cat.icon className="w-3 h-3" />
                                                            {cat.label}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    {/* Fiyat TL */}
                                    <td className="p-4">
                                        {editingCell?.productId === product.id && editingCell?.field === 'price_tl' ? (
                                            <Input
                                                autoFocus
                                                type="number"
                                                step="0.01"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => handleEditKeyDown(e, product, 'price_tl')}
                                                onBlur={() => inlineSave(product, 'price_tl', editValue)}
                                                className="h-8 w-24 text-sm"
                                            />
                                        ) : (
                                            <span className="cursor-pointer hover:text-blue-600"
                                                onClick={() => startEdit(product.id, 'price_tl', product.price_tl)}>
                                                {product.price_tl > 0 ? `${product.price_tl.toFixed(2)} ₺` : '-'}
                                            </span>
                                        )}
                                    </td>
                                    {/* Fiyat EUR - Otomatik (sadece okuma) */}
                                    <td className="p-4">
                                        <span className="text-slate-700" title="TCMB kurundan otomatik hesaplanir">
                                            {product.price_eur > 0 ? `${product.price_eur.toFixed(2)} €` : '-'}
                                        </span>
                                    </td>
                                    {/* Porsiyon - inline edit */}
                                    <td className="p-4">
                                        {editingCell?.productId === product.id && editingCell?.field === 'porsiyon' ? (
                                            <Input
                                                autoFocus
                                                type="number"
                                                step="0.01"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onKeyDown={(e) => handleEditKeyDown(e, product, 'porsiyon')}
                                                onBlur={() => inlineSave(product, 'porsiyon', editValue)}
                                                className="h-8 w-20 text-sm"
                                                data-testid={`edit-porsiyon-${product.id}`}
                                            />
                                        ) : (
                                            <span className="cursor-pointer hover:text-blue-600"
                                                onClick={() => startEdit(product.id, 'porsiyon', product.porsiyon ?? '')}
                                                data-testid={`porsiyon-cell-${product.id}`}>
                                                {product.porsiyon != null && product.porsiyon !== '' ? product.porsiyon : '-'}
                                            </span>
                                        )}
                                    </td>
                                    {/* İşlemler */}
                                    <td className="p-4 text-right">
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="text-red-500 hover:text-red-600"
                                            onClick={() => handleDelete(product)}
                                            data-testid={`delete-product-${product.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>
                            {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Sedna Ürün Kodu</Label>
                            <Input
                                value={formData.sedna_code}
                                onChange={(e) => setFormData({...formData, sedna_code: e.target.value})}
                                placeholder="örn. 1000003"
                                className="font-mono"
                                data-testid="sedna-code-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Ürün Adı *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Örn: Coca Cola"
                                data-testid="product-name-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Birim *</Label>
                                <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {UNIT_OPTIONS.map(unit => (
                                            <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Kategori *</Label>
                                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORY_OPTIONS.map(cat => (
                                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Fiyat (TL)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.price_tl}
                                    onChange={(e) => handlePriceTLChange(e.target.value)}
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fiyat (EUR) <span className="text-xs text-slate-400">(Otomatik: {tcmbRate} ₺)</span></Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.price_eur}
                                    readOnly
                                    placeholder="0.00"
                                    className="bg-slate-100 cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Porsiyon <span className="text-xs text-slate-400">(1 birimden kaç porsiyon)</span></Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={formData.porsiyon}
                                onChange={(e) => setFormData({...formData, porsiyon: e.target.value})}
                                placeholder="Örn: 4 (1 şişeden 4 porsiyon)"
                                data-testid="form-porsiyon"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Notlar</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Ek notlar..."
                                rows={2}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleSubmit}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                            data-testid="save-product-btn"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {editingProduct ? 'Güncelle' : 'Kaydet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Dialog */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5" />
                            Toplu Ürün Ekle
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="bg-slate-50 p-4 rounded-lg text-sm">
                            <p className="font-medium mb-2">Format:</p>
                            <code className="text-xs bg-white p-2 block rounded border">
                                Ürün Adı; Birim (cl/litre/paket/kg/adet); Kategori (alkollü/alkolsüz/soft); Fiyat TL; Fiyat EUR
                            </code>
                            <p className="mt-2 text-slate-500">
                                Her satır bir ürün. Kolonları noktalı virgül (;) veya TAB ile ayırın.
                            </p>
                        </div>

                        <div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".txt,.csv,.tsv,.xlsx,.xls"
                                className="hidden"
                            />
                            <Button 
                                variant="outline" 
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                Dosya Yükle (.xlsx, .csv)
                            </Button>
                        </div>

                        <div className="space-y-2">
                            <Label>Veya veriyi yapıştırın:</Label>
                            <Textarea
                                value={importData}
                                onChange={(e) => setImportData(e.target.value)}
                                placeholder="Coca Cola; cl; soft; 5.00; 0.15&#10;Efes Pilsen; cl; alkollü; 12.00; 0.35"
                                rows={8}
                                className="font-mono text-sm"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleBulkImport}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            {importData.split('\n').filter(l => l.trim()).length} Ürün Ekle
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Products;
